/**
 * @fileoverview Live Support Floating Widget
 *
 * A persistent floating widget that shows during active live support sessions.
 * Allows users to navigate the app while maintaining their support session.
 *
 * Features:
 * - Minimized pill view showing session status
 * - Expandable to full controls
 * - Draggable positioning
 * - Persists across page navigation
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiMonitor,
  FiCopy,
  FiCheck,
  FiX,
  FiLoader,
  FiUserCheck,
  FiVideo,
  FiVideoOff,
  FiClock,
  FiChevronUp,
  FiChevronDown,
  FiMaximize2,
  FiInfo
} from 'react-icons/fi';
import { useLiveSupport } from '../context/LiveSupportContext';
import Modal from './shared/Modal';
import LiveSupportModal from './LiveSupportModal';
import './LiveSupportWidget.css';

// Check if screen sharing is supported (not available on mobile)
const isScreenShareSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
};

/**
 * LiveSupportWidget Component
 *
 * Floating widget that shows when there's an active support session.
 */
const LiveSupportWidget = () => {
  const {
    session,
    status,
    startScreenShare,
    stopScreenShare,
    endSession,
    isConnected,
    isSharing,
    hasActiveSession,
    sessionEndedByAdmin,
    clearSessionEndedByAdmin,
  } = useLiveSupport();

  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullModal, setShowFullModal] = useState(false);
  const [showEndedModal, setShowEndedModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [position, setPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const widgetRef = useRef(null);
  const canShareScreen = isScreenShareSupported();

  // Session timer
  useEffect(() => {
    if (!session || status === 'idle') {
      setSessionDuration(0);
      return;
    }

    const startTime = new Date(session.created_at).getTime();
    const updateDuration = () => {
      const now = Date.now();
      setSessionDuration(Math.floor((now - startTime) / 1000));
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [session, status]);

  // Format duration as mm:ss
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Copy code to clipboard
  const handleCopyCode = useCallback(async () => {
    if (!session?.code) return;

    try {
      await navigator.clipboard.writeText(session.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [session?.code]);

  // Handle dragging
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.widget-action-btn') || e.target.closest('.widget-expand-btn')) {
      return;
    }
    setIsDragging(true);
    setHasDragged(false);
    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    setHasDragged(true);

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep within viewport bounds
    const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 300);
    const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 100);

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle header click - only toggle if not dragging
  const handleHeaderClick = useCallback(() => {
    if (!hasDragged) {
      setIsExpanded(prev => !prev);
    }
    setHasDragged(false);
  }, [hasDragged]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Get status info for display
  const getStatusIcon = () => {
    switch (status) {
      case 'generating':
      case 'connecting':
        return <FiLoader className="spin" />;
      case 'waiting':
        return <FiMonitor />;
      case 'connected':
        return <FiUserCheck />;
      case 'sharing':
        return <FiVideo />;
      default:
        return <FiMonitor />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'generating':
        return 'Generating...';
      case 'waiting':
        return 'Waiting for agent';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Agent connected';
      case 'sharing':
        return 'Sharing screen';
      default:
        return 'Support session';
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'connected':
      case 'sharing':
        return 'status-success';
      case 'waiting':
        return 'status-warning';
      case 'error':
        return 'status-error';
      default:
        return '';
    }
  };

  // Show modal when admin ends the session
  useEffect(() => {
    if (sessionEndedByAdmin) {
      setShowEndedModal(true);
      clearSessionEndedByAdmin();
    }
  }, [sessionEndedByAdmin, clearSessionEndedByAdmin]);

  // Don't render widget if no active session (but still render the ended modal)
  if (!hasActiveSession) {
    return (
      <Modal
        isOpen={showEndedModal}
        onClose={() => setShowEndedModal(false)}
        size="sm"
        className="session-ended-modal"
      >
        <div className="session-ended-content">
          <div className="session-ended-icon">
            <FiInfo />
          </div>
          <h2>Session Ended</h2>
          <p>
            Your support session has been ended by the support agent.
            Thank you for contacting support!
          </p>
          <button
            className="btn btn-primary session-ended-btn"
            onClick={() => setShowEndedModal(false)}
          >
            OK
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <div
        ref={widgetRef}
        className={`live-support-widget ${isExpanded ? 'expanded' : ''} ${getStatusClass()} ${isDragging ? 'dragging' : ''}`}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        {/* Minimized view */}
        <div className="widget-header" onClick={handleHeaderClick}>
          <div className="widget-status">
            {getStatusIcon()}
            <span className="widget-status-text">{getStatusText()}</span>
          </div>
          <div className="widget-header-actions">
            <span className="widget-timer">
              <FiClock />
              {formatDuration(sessionDuration)}
            </span>
            <button
              className="widget-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? <FiChevronDown /> : <FiChevronUp />}
            </button>
          </div>
        </div>

        {/* Expanded view */}
        {isExpanded && (
          <div className="widget-content">
            {/* Support code */}
            {session?.code && (
              <div className="widget-code-section">
                <span className="widget-code-label">Code:</span>
                <span className="widget-code-value">{session.code}</span>
                <button
                  className="widget-action-btn copy"
                  onClick={handleCopyCode}
                  title="Copy code"
                >
                  {copied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="widget-actions">
              {isConnected && !isSharing && canShareScreen && (
                <button
                  className="widget-action-btn primary"
                  onClick={startScreenShare}
                >
                  <FiVideo />
                  Share Screen
                </button>
              )}

              {isConnected && !isSharing && !canShareScreen && (
                <span className="widget-mobile-notice">
                  Screen sharing not available on mobile
                </span>
              )}

              {isSharing && (
                <button
                  className="widget-action-btn warning"
                  onClick={stopScreenShare}
                >
                  <FiVideoOff />
                  Stop Sharing
                </button>
              )}

              <button
                className="widget-action-btn secondary"
                onClick={() => setShowFullModal(true)}
                title="Expand to full view"
              >
                <FiMaximize2 />
              </button>

              <button
                className="widget-action-btn danger"
                onClick={endSession}
                title="End session"
              >
                <FiX />
              </button>
            </div>

            {/* Sharing indicator */}
            {isSharing && (
              <div className="widget-sharing-indicator">
                <div className="pulse-dot"></div>
                <span>Screen sharing active</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full modal for detailed view */}
      <LiveSupportModal
        isOpen={showFullModal}
        onClose={() => setShowFullModal(false)}
      />
    </>
  );
};

export default LiveSupportWidget;
